import React, { useState, useEffect, useRef } from 'react';
import { getApiBase } from './config/apiBase';
import { authService } from './services/authService';
import useDictation from './hooks/useDictation';
import TemplateLibrary from './TemplateLibrary';
import { extractDocxText, extractDocxTemplate } from './utils/docx';

const apiUrl = getApiBase();

/** Read an uploaded document to text, handling .docx via jszip and the rest as plain text. */
const readDocFile = (file) => {
  if (file.name.toLowerCase().endsWith('.docx')) return extractDocxText(file);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(String(e.target.result || ''));
    reader.onerror = () => reject(new Error('failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * QA Docs generator — builds SRS / SDS (and other QA documents) from JIRA work
 * items, GitHub repository files, and uploaded documents using a selectable or
 * user-supplied template. Renders a Markdown preview and supports PDF download.
 */
const QaDocForm = ({
  selectedProject,
  selectedModel,
  userInfo,
  // Flow integration (all optional — component works standalone without them):
  lockedDocType = null,      // force a specific template/docType and hide the picker
  flowStep = null,           // flow-definition step: { name, inputs, output, ... }
  initialDocument = null,    // preloaded saved deliverable { markdown, docControl, status }
  onSaveDeliverable = null,  // async (docType, payload) => savedFlow; enables "Save to flow"
  flowName = '',             // label for the save button
  defaultJiraKeys = [],      // JIRA keys to pre-select as sources (e.g. SRS -> parent Epic)
}) => {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(lockedDocType || '');
  const [customTemplate, setCustomTemplate] = useState(null);
  const [customTemplateName, setCustomTemplateName] = useState('');
  // Saved user/org templates from the library, plus the currently-selected one
  // (when the active template comes from the library rather than a built-in).
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedSavedId, setSelectedSavedId] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryPrefill, setLibraryPrefill] = useState(null);
  const [mode, setMode] = useState(initialDocument?.markdown ? 'review' : 'generate'); // 'generate' | 'review'
  // Existing document to revise (review mode). Seeded from a saved deliverable
  // when editing one in the flow.
  const [baseDoc, setBaseDoc] = useState(() => (initialDocument?.markdown
    ? { name: `${lockedDocType || 'Document'} (saved)`, content: initialDocument.markdown }
    : null));

  // Free-text notes / dictated "Raw Speech" folded into generation as context.
  const [notes, setNotes] = useState('');
  const notesBaseRef = useRef('');

  // Editable copy of the generated/loaded document (for "edit old outputs") and
  // the save-to-flow status.
  const [editMarkdown, setEditMarkdown] = useState(() => initialDocument?.markdown || '');
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  // Input sources
  // JIRA tickets — searchable, project-scoped picker that mirrors Design Output.
  const [projectIssues, setProjectIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [issuesError, setIssuesError] = useState(null);
  const [jiraSearch, setJiraSearch] = useState('');
  const [jiraTypeFilter, setJiraTypeFilter] = useState('');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState([]);

  // GitHub — searchable repo + branch + navigable file browser.
  const [repos, setRepos] = useState([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [repoSearch, setRepoSearch] = useState('');
  const [selectedRepo, setSelectedRepo] = useState(''); // "owner/repo"
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [ghPath, setGhPath] = useState('');
  const [ghDirItems, setGhDirItems] = useState([]);
  const [loadingDir, setLoadingDir] = useState(false);
  const [ghDirError, setGhDirError] = useState(null);
  const [ghFileFilter, setGhFileFilter] = useState('');
  const [ghExtFilter, setGhExtFilter] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [autoSelectRepo, setAutoSelectRepo] = useState(true);

  // Document control metadata (optional) — for controlled QMS documents such as
  // a VEP, whose header carries doc number/revision and whose body reuses the
  // product token and related-document IDs throughout.
  const [showDocControl, setShowDocControl] = useState(Boolean(initialDocument?.docControl));
  const [docControl, setDocControl] = useState(() => ({
    documentTitle: '', documentNumber: '', revision: '', effectiveDate: '',
    systemName: '', manufacturer: '', versionBuild: '', productToken: '',
    srsReference: '', verificationReportId: '',
    ...(initialDocument?.docControl || {}),
  }));
  const setDc = (key, value) => setDocControl((prev) => ({ ...prev, [key]: value }));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [sources, setSources] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);
  // Download filename prompt: { format: 'pdf'|'docx' } while open, else null.
  const [downloadPrompt, setDownloadPrompt] = useState(null);
  const [filenameInput, setFilenameInput] = useState('');

  const templateInputRef = useRef(null);
  const docsInputRef = useRef(null);
  const baseDocInputRef = useRef(null);

  const authenticatedFetch = async (url, options = {}) => {
    const token = await authService.getAccessToken();
    return fetch(url, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
  };

  // Voice dictation for the notes / "Raw Speech" field — reuses the same Web
  // Speech hook as the Build-from-scratch tool. Final chunks are appended.
  const dictation = useDictation({
    onResult: (text, { isFinal }) => {
      if (!isFinal) return;
      setNotes((prev) => {
        const sep = prev && !/\s$/.test(prev) ? ' ' : '';
        const next = `${prev}${sep}${text}`;
        notesBaseRef.current = next;
        return next;
      });
    },
  });

  // Load built-in document templates on mount.
  useEffect(() => {
    (async () => {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/qa/doc-templates`);
        const data = await res.json();
        if (data.success && Array.isArray(data.templates)) {
          setTemplates(data.templates);
          if (data.templates.length && !templateId) setTemplateId(data.templates[0].id);
        }
      } catch (err) {
        console.error('[QA-DOCS] Failed to load templates:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the saved template library (own private + all shared).
  const loadSavedTemplates = async () => {
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/template-library`);
      const data = await res.json();
      if (data.success && Array.isArray(data.templates)) setSavedTemplates(data.templates);
    } catch (err) {
      console.error('[QA-DOCS] Failed to load saved templates:', err);
    }
  };
  useEffect(() => {
    loadSavedTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the selected project's JIRA tickets for the searchable picker. Mirrors
  // the Design Output tool: fetch all tickets once, then filter client-side.
  useEffect(() => {
    setSelectedTickets([]);
    setJiraSearch('');
    setJiraTypeFilter('');
    if (!selectedProject) {
      setProjectIssues([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingIssues(true);
      setIssuesError(null);
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/search-issues?projectKey=${encodeURIComponent(selectedProject)}&query=`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success) {
          setProjectIssues(data.issues || []);
        } else {
          setIssuesError(data.error || `Failed to load tickets (${res.status})`);
        }
      } catch (err) {
        if (!cancelled) setIssuesError(err.message || 'Failed to load tickets');
      } finally {
        if (!cancelled) setLoadingIssues(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // Pre-select the flow-provided default JIRA source(s) once the project tickets
  // load — e.g. the SRS step seeds the parent Epic from the JIRA Epic step so the
  // user doesn't have to pick it again. Seeds only a fresh generate (not when
  // editing a saved document) and only once per mount.
  const didSeedJiraRef = useRef(false);
  useEffect(() => {
    if (didSeedJiraRef.current) return;
    if (initialDocument?.markdown) return;           // editing a saved draft — leave as-is
    if (!defaultJiraKeys || defaultJiraKeys.length === 0) return;
    if (projectIssues.length === 0) return;          // wait until tickets are available
    const seeded = defaultJiraKeys
      .map((key) => projectIssues.find((i) => i.key === key))
      .filter(Boolean)
      .map((issue) => ({ key: issue.key, summary: issue.fields?.summary || '', type: issue.fields?.issuetype?.name || '' }));
    if (seeded.length === 0) return;
    didSeedJiraRef.current = true;
    setSelectedTickets((prev) => {
      const existing = new Set(prev.map((t) => t.key));
      return [...prev, ...seeded.filter((t) => !existing.has(t.key))];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIssues, defaultJiraKeys, initialDocument?.markdown]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingRepos(true);
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/github/repos`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.repos)) {
          const formatted = data.repos.map((r) => ({
            fullName: r.full_name || `${typeof r.owner === 'object' ? r.owner.login : r.owner}/${r.name}`,
            name: r.name,
            owner: typeof r.owner === 'object' ? r.owner.login : r.owner,
            defaultBranch: r.default_branch || '',
          })).sort((a, b) => a.fullName.localeCompare(b.fullName));
          setRepos(formatted);
        }
      } catch (err) {
        console.error('[QA-DOCS] Failed to load repos:', err);
      } finally {
        if (!cancelled) setLoadingRepos(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When the repo changes, reset the browser and load its branches.
  useEffect(() => {
    setBranches([]);
    setGhPath('');
    setGhDirItems([]);
    setSelectedFiles([]);
    setGhFileFilter('');
    setGhExtFilter('');
    if (!selectedRepo) {
      setSelectedBranch('');
      return undefined;
    }
    const repo = repos.find((r) => r.fullName === selectedRepo);
    setSelectedBranch(repo?.defaultBranch || '');
    let cancelled = false;
    (async () => {
      setLoadingBranches(true);
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/github/repos/${selectedRepo}/branches`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.branches)) {
          setBranches(data.branches);
        }
      } catch (err) {
        console.error('[QA-DOCS] Failed to load branches:', err);
      } finally {
        if (!cancelled) setLoadingBranches(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepo]);

  // Load the current directory listing whenever repo/branch/path changes.
  useEffect(() => {
    if (!selectedRepo) {
      setGhDirItems([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoadingDir(true);
      setGhDirError(null);
      try {
        const params = new URLSearchParams();
        if (selectedBranch) params.append('ref', selectedBranch);
        if (ghPath) params.append('path', ghPath);
        const qs = params.toString();
        const res = await authenticatedFetch(`${apiUrl}/api/github/repos/${selectedRepo}/contents${qs ? `?${qs}` : ''}`);
        const data = await res.json();
        if (cancelled) return;
        if (res.ok && data.success && Array.isArray(data.contents)) {
          setGhDirItems(data.contents);
        } else {
          setGhDirError(data.error || `Failed to load contents (${res.status})`);
          setGhDirItems([]);
        }
      } catch (err) {
        if (!cancelled) { setGhDirError(err.message || 'Failed to load contents'); setGhDirItems([]); }
      } finally {
        if (!cancelled) setLoadingDir(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRepo, selectedBranch, ghPath]);

  // Upload a one-off custom template: either a .json section list, or a .docx
  // "golden example" (parsed into an exemplar the AI matches + heading sections).
  const handleTemplateUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const isDocx = file.name.toLowerCase().endsWith('.docx');
    try {
      if (isDocx) {
        const { exemplar, sections } = await extractDocxTemplate(file);
        if (!exemplar) throw new Error('could not read any text from the .docx');
        const secs = sections.length
          ? sections
          : [{ title: 'Document', guidance: 'Follow the structure and formatting of the example document.' }];
        setCustomTemplate({ name: file.name.replace(/\.docx$/i, ''), sections: secs, exemplar, format: 'docx' });
      } else {
        const parsed = JSON.parse(await readDocFile(file));
        if (!parsed || !Array.isArray(parsed.sections) || !parsed.sections.length) {
          throw new Error('Expected { name, sections: [{ title, guidance }] }');
        }
        setCustomTemplate({ ...parsed, format: 'sections' });
      }
      setCustomTemplateName(file.name);
      setSelectedSavedId(null);
      setError(null);
    } catch (err) {
      setError(`Invalid template file: ${err.message}`);
      setCustomTemplate(null);
      setCustomTemplateName('');
      if (templateInputRef.current) templateInputRef.current.value = '';
    }
  };

  const clearCustomTemplate = () => {
    setCustomTemplate(null);
    setCustomTemplateName('');
    setSelectedSavedId(null);
    if (templateInputRef.current) templateInputRef.current.value = '';
  };

  const handleDocsUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    for (const file of files) {
      try {
        const content = await readDocFile(file);
        setUploadedDocs((prev) => [...prev, { name: file.name, content }]);
      } catch (err) {
        setError(`Could not read ${file.name}: ${err.message}`);
      }
    }
    if (docsInputRef.current) docsInputRef.current.value = '';
  };

  const removeDoc = (idx) => setUploadedDocs((prev) => prev.filter((_, i) => i !== idx));

  const handleBaseDocUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const content = await readDocFile(file);
      setBaseDoc({ name: file.name, content });
    } catch (err) {
      setError(`Could not read ${file.name}: ${err.message}`);
    }
  };

  const clearBaseDoc = () => {
    setBaseDoc(null);
    if (baseDocInputRef.current) baseDocInputRef.current.value = '';
  };

  const addTicket = (key) => {
    if (!key) return;
    const issue = projectIssues.find((i) => i.key === key);
    if (!issue) return;
    setSelectedTickets((prev) => (prev.some((t) => t.key === key)
      ? prev
      : [...prev, { key: issue.key, summary: issue.fields?.summary || '', type: issue.fields?.issuetype?.name || '' }]));
  };

  const removeTicket = (key) => setSelectedTickets((prev) => prev.filter((t) => t.key !== key));

  const openDir = (path) => { setGhFileFilter(''); setGhExtFilter(''); setGhPath(path); };
  const addFile = (path) => setSelectedFiles((prev) => (prev.includes(path) ? prev : [...prev, path]));
  const removeFile = (path) => setSelectedFiles((prev) => prev.filter((p) => p !== path));

  const handleGenerate = async () => {
    setError(null);
    setResult(null);
    setSources(null);
    setSaveState('idle');
    if (dictation.listening) dictation.stop();

    const ticketKeys = selectedTickets.map((t) => t.key);
    const epicKeys = selectedTickets.filter((t) => (t.type || '').toLowerCase() === 'epic').map((t) => t.key);
    const [ghOwner, ghRepoName] = selectedRepo ? selectedRepo.split('/') : ['', ''];

    // Fold the free-text notes / dictated speech in as a synthetic reference doc.
    const trimmedNotes = notes.trim();
    const docsForGen = trimmedNotes
      ? [...uploadedDocs, { name: 'Notes / Raw speech', content: trimmedNotes }]
      : uploadedDocs;

    const hasJira = ticketKeys.length > 0;
    const hasGithub = Boolean(selectedRepo) && (autoSelectRepo || selectedFiles.length > 0);
    const hasDocs = docsForGen.length > 0;
    const isReview = mode === 'review';

    if (isReview && !baseDoc) {
      setError('Upload the document you want to review/update.');
      return;
    }
    if (!isReview && !templateId && !customTemplate) {
      setError('Select a document template or upload a custom one.');
      return;
    }
    if (!isReview && !hasJira && !hasGithub && !hasDocs) {
      if (selectedRepo) {
        setError('Select files to include, or turn on “Auto-analyze the whole repo” to let the AI pick them.');
      } else {
        setError('Provide at least one input source: JIRA tickets, GitHub files, uploaded documents, or notes.');
      }
      return;
    }

    const dcEntries = Object.entries(docControl).filter(([, v]) => String(v).trim() !== '');
    const docControlPayload = dcEntries.length
      ? Object.fromEntries(dcEntries.map(([k, v]) => [k, String(v).trim()]))
      : undefined;

    const body = {
      mode,
      templateId: customTemplate ? undefined : templateId,
      customTemplate: customTemplate || undefined,
      selectedModel,
      baseDocument: isReview ? baseDoc : undefined,
      docControl: docControlPayload,
      jira: hasJira ? { ticketKeys, epicKey: epicKeys[0] } : undefined,
      github: hasGithub ? { owner: ghOwner, repo: ghRepoName, ref: selectedBranch || undefined, paths: selectedFiles, autoSelect: autoSelectRepo } : undefined,
      uploadedDocs: hasDocs ? docsForGen : undefined,
    };

    setLoading(true);
    try {
      const res = await authenticatedFetch(`${apiUrl}/api/qa/generate-document`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      setResult(data.document);
      setEditMarkdown(data.document.markdown || '');
      setSources(data.sources);
    } catch (err) {
      setError(err.message || 'Failed to generate document');
    } finally {
      setLoading(false);
    }
  };

  // Persist the current (possibly edited) document into the active flow.
  const handleSaveToFlow = async () => {
    if (!onSaveDeliverable || !lockedDocType) return;
    const markdown = editMarkdown.trim();
    if (!markdown) {
      setError('Nothing to save — generate or write the document first.');
      return;
    }
    setError(null);
    setSaveState('saving');
    const dcEntries = Object.entries(docControl).filter(([, v]) => String(v).trim() !== '');
    const docControlPayload = dcEntries.length
      ? Object.fromEntries(dcEntries.map(([k, v]) => [k, String(v).trim()]))
      : null;
    try {
      await onSaveDeliverable(lockedDocType, {
        markdown,
        docControl: docControlPayload,
        sources: sources || null,
        modelUsed: result?.modelUsed || null,
        status: 'complete',
      });
      setSaveState('saved');
    } catch (err) {
      setError(err.message || 'Failed to save to flow');
      setSaveState('error');
    }
  };

  // Default base filename offered in the download prompt: the entered document
  // number if present, otherwise a template-based placeholder like ISV-R-XXX-SDS.
  const defaultDownloadName = () => {
    const num = (docControl.documentNumber || '').trim();
    if (num) return num;
    const code = (lockedDocType || result?.docType || 'DOC').toUpperCase();
    return `ISV-R-XXX-${code}`;
  };

  // Turn a user-entered name into a safe filename with the right extension.
  const sanitizeFilename = (name, ext) => {
    let n = String(name || '').trim().replace(new RegExp(`\\.${ext}$`, 'i'), '');
    n = n.replace(/[^A-Za-z0-9\-_. ]+/g, '_').replace(/\s+/g, '_').replace(/_{2,}/g, '_').replace(/^[_.]+|[_.]+$/g, '');
    return `${n || 'document'}.${ext}`;
  };

  // Ask the user for a filename before downloading, prefilled with the default.
  const openDownloadPrompt = (format) => {
    setError(null);
    setFilenameInput(defaultDownloadName());
    setDownloadPrompt({ format });
  };

  const confirmDownload = () => {
    if (!downloadPrompt) return;
    const { format } = downloadPrompt;
    const name = filenameInput;
    setDownloadPrompt(null);
    if (format === 'docx') performDownloadDocx(name);
    else performDownloadPdf(name);
  };

  const performDownloadPdf = async (filenameBase) => {
    const md = (editMarkdown && editMarkdown.trim()) || result?.markdown;
    if (!md) return;
    setPdfLoading(true);
    setError(null);
    try {
      const title = `${lockedDocType || result?.docType || 'document'}${selectedProject ? `-${selectedProject}` : ''}`;
      const res = await authenticatedFetch(`${apiUrl}/api/qa/document/pdf`, {
        method: 'POST',
        body: JSON.stringify({ markdown: md, title }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `PDF request failed (${res.status})`);
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = sanitizeFilename(filenameBase, 'pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err.message || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCopyMarkdown = () => {
    const md = (editMarkdown && editMarkdown.trim()) || result?.markdown;
    if (md) navigator.clipboard.writeText(md);
  };

  const performDownloadDocx = async (filenameBase) => {
    const md = (editMarkdown && editMarkdown.trim()) || result?.markdown;
    if (!md) return;
    setDocxLoading(true);
    setError(null);
    try {
      const title = `${lockedDocType || result?.docType || 'document'}${selectedProject ? `-${selectedProject}` : ''}`;
      const dcEntries = Object.entries(docControl).filter(([, v]) => String(v).trim() !== '');
      const docControlPayload = dcEntries.length ? Object.fromEntries(dcEntries) : undefined;
      const res = await authenticatedFetch(`${apiUrl}/api/qa/document/docx`, {
        method: 'POST',
        body: JSON.stringify({ markdown: md, title, docControl: docControlPayload }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `DOCX request failed (${res.status})`);
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = sanitizeFilename(filenameBase, 'docx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      setError(err.message || 'Failed to download DOCX');
    } finally {
      setDocxLoading(false);
    }
  };

  const activeTemplate = templates.find((t) => t.id === templateId);
  const activeSavedTemplate = savedTemplates.find((t) => t.id === selectedSavedId) || null;

  // Pick a saved library template as the active template. The generator receives
  // it as `customTemplate`, so it works identically to an uploaded template. For
  // docx templates the list omits the (large) exemplar, so fetch the full record.
  const applySavedTemplate = async (t) => {
    if (!t) return;
    let full = t;
    if ((t.format === 'docx' || t.hasExemplar) && !t.exemplar) {
      try {
        const res = await authenticatedFetch(`${apiUrl}/api/qa/template-library/${t.id}`);
        const data = await res.json();
        if (res.ok && data.success && data.template) full = data.template;
      } catch (err) {
        console.error('[QA-DOCS] Failed to load template exemplar:', err);
      }
    }
    setCustomTemplate({
      name: full.name,
      sections: full.sections,
      ...(full.exemplar ? { exemplar: full.exemplar, format: 'docx' } : {}),
    });
    setCustomTemplateName(full.name);
    setSelectedSavedId(full.id);
    setTemplateId('');
    setError(null);
    if (templateInputRef.current) templateInputRef.current.value = '';
  };

  // Handle the built-in / saved template dropdown. Values are 'builtin:<id>' or
  // 'saved:<id>'.
  const onSelectTemplate = (value) => {
    if (value.startsWith('saved:')) {
      applySavedTemplate(savedTemplates.find((t) => t.id === value.slice(6)));
    } else if (value.startsWith('builtin:')) {
      setTemplateId(value.slice(8));
      setCustomTemplate(null);
      setCustomTemplateName('');
      setSelectedSavedId(null);
      if (templateInputRef.current) templateInputRef.current.value = '';
    }
  };

  // The dropdown's current value (built-in id or saved id).
  const templateSelectValue = selectedSavedId ? `saved:${selectedSavedId}` : `builtin:${templateId}`;

  // Open the library editor prefilled from the current template, to save it as a
  // reusable library template.
  const saveCurrentAsTemplate = () => {
    const src = customTemplate || activeTemplate;
    const baseName = src?.name || activeTemplate?.name || lockedDocType || 'Document';
    const isDocx = customTemplate?.format === 'docx' && customTemplate?.exemplar;
    setLibraryPrefill({
      name: `${baseName} (copy)`,
      docType: lockedDocType || activeTemplate?.docType || 'generic',
      description: activeTemplate?.description || '',
      visibility: 'private',
      sections: (src?.sections || []).map((s) => ({ title: s.title || '', guidance: s.guidance || '' })),
      ...(isDocx ? { format: 'docx', exemplar: customTemplate.exemplar } : {}),
    });
    setLibraryOpen(true);
  };

  const openLibrary = () => {
    setLibraryPrefill(null);
    setLibraryOpen(true);
  };

  // Saved templates offered on a flow step: those tagged for this deliverable or
  // generic ones usable anywhere.
  const flowSavedTemplates = savedTemplates.filter(
    (t) => t.docType === lockedDocType || t.docType === 'generic',
  );

  // Issue types actually present in the loaded tickets (Epic first), so the type
  // dropdown only offers types that exist in this project.
  const availableTypes = (() => {
    const set = new Set();
    projectIssues.forEach((issue) => {
      const t = issue?.fields?.issuetype?.name;
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => {
      if (a.toLowerCase() === 'epic') return -1;
      if (b.toLowerCase() === 'epic') return 1;
      return a.localeCompare(b);
    });
  })();

  // Filter the loaded tickets by type + search box and hide already-selected
  // ones, then group by issue type (Epic first) for the picker dropdown.
  const jiraQuery = jiraSearch.trim().toLowerCase();
  const filteredIssues = projectIssues.filter((issue) => {
    if (!issue || !issue.key || !issue.fields) return false;
    if (selectedTickets.some((t) => t.key === issue.key)) return false;
    if (jiraTypeFilter && (issue.fields.issuetype?.name || '') !== jiraTypeFilter) return false;
    if (!jiraQuery) return true;
    return (
      issue.key.toLowerCase().includes(jiraQuery) ||
      (issue.fields.summary || '').toLowerCase().includes(jiraQuery)
    );
  });
  const groupedTicketOptions = (() => {
    const groups = filteredIssues.reduce((acc, issue) => {
      const type = issue.fields?.issuetype?.name || 'Other';
      (acc[type] = acc[type] || []).push(issue);
      return acc;
    }, {});
    const types = Object.keys(groups).sort((a, b) => {
      if (a.toLowerCase() === 'epic') return -1;
      if (b.toLowerCase() === 'epic') return 1;
      return a.localeCompare(b);
    });
    return types.map((type) => (
      <optgroup key={type} label={`${type} (${groups[type].length})`}>
        {groups[type].map((issue) => (
          <option key={issue.key} value={issue.key}>
            {issue.key} — {issue.fields.summary}
          </option>
        ))}
      </optgroup>
    ));
  })();

  // GitHub repo dropdown filtering + the directory browser's split/derived data.
  const repoQuery = repoSearch.trim().toLowerCase();
  const filteredRepos = repos.filter((r) => !repoQuery || r.fullName.toLowerCase().includes(repoQuery));

  const extOf = (name) => {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot).toLowerCase() : '(no ext)';
  };
  const dirEntries = ghDirItems.filter((it) => it.type === 'dir').sort((a, b) => a.name.localeCompare(b.name));
  const fileEntries = ghDirItems.filter((it) => it.type === 'file').sort((a, b) => a.name.localeCompare(b.name));
  const fileExtensions = Array.from(new Set(fileEntries.map((f) => extOf(f.name)))).sort();
  const ghFileQuery = ghFileFilter.trim().toLowerCase();
  const visibleFiles = fileEntries.filter((f) => {
    if (ghExtFilter && extOf(f.name) !== ghExtFilter) return false;
    return !ghFileQuery || f.name.toLowerCase().includes(ghFileQuery);
  });
  const pathSegments = ghPath ? ghPath.split('/') : [];

  return (
    <div style={styles.container}>
      {lockedDocType ? (
        <>
          <h2 style={styles.title}>{flowStep?.name || lockedDocType}</h2>
          <p style={styles.subtitle}>
            Deliverable {flowStep?.order ? `#${flowStep.order}` : ''} in this flow — generate a new version or edit the saved one, then save it back to the flow.
          </p>
          {flowStep && (
            <div style={styles.inputHints}>
              {flowStep.inputs?.required?.length > 0 && (
                <div style={styles.hintRow}>
                  <span style={styles.hintReqLabel}>Required inputs</span>
                  {flowStep.inputs.required.map((x) => (
                    <span key={x} style={styles.hintReq}>{x}</span>
                  ))}
                </div>
              )}
              {flowStep.inputs?.optional?.length > 0 && (
                <div style={styles.hintRow}>
                  <span style={styles.hintOptLabel}>Optional inputs</span>
                  {flowStep.inputs.optional.map((x) => (
                    <span key={x} style={styles.hintOpt}>{x}</span>
                  ))}
                </div>
              )}
              {flowStep.output && <p style={styles.help}><strong>Output:</strong> {flowStep.output}</p>}
            </div>
          )}
        </>
      ) : (
        <>
          <h2 style={styles.title}>📑 QA Document Generator</h2>
          <p style={styles.subtitle}>
            Generate a new SRS/SDS/custom QA document, or review and update an existing one — using JIRA, a GitHub repository, and/or uploaded documents.
            {userInfo?.name ? ` Signed in as ${userInfo.name}.` : ''}
          </p>
        </>
      )}

      {/* Mode toggle */}
      <div style={styles.modeToggle}>
        <button
          style={{ ...styles.modeBtn, ...(mode === 'generate' ? styles.modeBtnActive : {}) }}
          onClick={() => setMode('generate')}
        >
          ✨ Generate new
        </button>
        <button
          style={{ ...styles.modeBtn, ...(mode === 'review' ? styles.modeBtnActive : {}) }}
          onClick={() => setMode('review')}
        >
          📝 Review / update existing
        </button>
      </div>

      {/* Document to review (review mode only) */}
      {mode === 'review' && (
        <div style={styles.section}>
          <label style={styles.label}>Document to review / update</label>
          {baseDoc ? (
            <div style={styles.pillRow}>
              <span style={styles.pill}>{baseDoc.name} ({baseDoc.content.length} chars)</span>
              <button style={styles.linkBtn} onClick={clearBaseDoc}>remove</button>
            </div>
          ) : (
            <input ref={baseDocInputRef} type="file" accept=".txt,.md,.json,.csv,.docx,text/*" onChange={handleBaseDocUpload} style={styles.fileInput} />
          )}
          <p style={styles.help}>The AI revises this document, fixing gaps and inconsistencies, and appends Reviewer Notes. JIRA, GitHub, and supporting documents below are used as reference.</p>
        </div>
      )}

      {/* Template. In flow mode the deliverable defaults to its built-in
          template, but a saved library template can be chosen instead. */}
      {lockedDocType ? (
        <div style={styles.section}>
          <label style={styles.label}>Template</label>
          <select style={styles.input} value={templateSelectValue} onChange={(e) => onSelectTemplate(e.target.value)}>
            <optgroup label="Built-in">
              {templates
                .filter((t) => t.id === lockedDocType || t.docType === lockedDocType)
                .map((t) => <option key={t.id} value={`builtin:${t.id}`}>{t.name}</option>)}
            </optgroup>
            {flowSavedTemplates.length > 0 && (
              <optgroup label="Saved templates">
                {flowSavedTemplates.map((t) => (
                  <option key={t.id} value={`saved:${t.id}`}>{t.name} ({t.visibility})</option>
                ))}
              </optgroup>
            )}
          </select>
          {selectedSavedId ? (
            <p style={styles.help}>
              Using your saved template “{activeSavedTemplate?.name}”.
              {activeSavedTemplate?.description ? ` ${activeSavedTemplate.description}` : ''}
            </p>
          ) : (
            <p style={styles.help}>
              <span style={styles.builtInBadge}>built-in ✓</span>{' '}
              Applied automatically — nothing to upload.
              {activeTemplate?.description ? ` ${activeTemplate.description}` : ''}
            </p>
          )}
          <div style={styles.uploadRow}>
            <button type="button" style={styles.linkBtn} onClick={openLibrary}>📚 Manage templates</button>
            <button type="button" style={styles.linkBtn} onClick={saveCurrentAsTemplate}>Save current as template</button>
          </div>
        </div>
      ) : (
        <div style={styles.section}>
          <label style={styles.label}>{mode === 'review' ? 'Target template (optional structure to align to)' : 'Document template'}</label>
          {customTemplate && !selectedSavedId ? (
            <div style={styles.pillRow}>
              <span style={styles.pill}>
                Uploaded: {customTemplateName}
                {customTemplate.format === 'docx' ? ' · matches this example’s style' : ''}
              </span>
              <button style={styles.linkBtn} onClick={clearCustomTemplate}>use built-in instead</button>
            </div>
          ) : (
            <select style={styles.input} value={templateSelectValue} onChange={(e) => onSelectTemplate(e.target.value)}>
              {templates.length === 0 && <option value="">Loading…</option>}
              <optgroup label="Built-in">
                {templates.map((t) => (
                  <option key={t.id} value={`builtin:${t.id}`}>{t.name}</option>
                ))}
              </optgroup>
              {savedTemplates.length > 0 && (
                <optgroup label="Saved templates">
                  {savedTemplates.map((t) => (
                    <option key={t.id} value={`saved:${t.id}`}>{t.name} ({t.visibility})</option>
                  ))}
                </optgroup>
              )}
            </select>
          )}
          {selectedSavedId && activeSavedTemplate?.description && (
            <p style={styles.help}>{activeSavedTemplate.description}</p>
          )}
          {!customTemplate && activeTemplate?.description && (
            <p style={styles.help}>{activeTemplate.description}</p>
          )}
          <div style={styles.uploadRow}>
            <button type="button" style={styles.linkBtn} onClick={openLibrary}>📚 Manage templates</button>
            <button type="button" style={styles.linkBtn} onClick={saveCurrentAsTemplate}>Save current as template</button>
          </div>
          <div style={styles.uploadRow}>
            <input ref={templateInputRef} type="file" accept=".json,application/json,.docx" onChange={handleTemplateUpload} style={styles.fileInput} />
            <span style={styles.help}>or upload a one-off custom template — a .json section list, or a .docx “golden example” the AI should match</span>
          </div>
        </div>
      )}

      {/* Document control (optional, for controlled QMS documents) */}
      <div style={styles.section}>
        <button
          type="button"
          style={styles.collapseHeader}
          onClick={() => setShowDocControl((v) => !v)}
        >
          {showDocControl ? '▾' : '▸'} Document control — optional, for controlled QMS documents (VEP, SRS, SDS)
        </button>
        {showDocControl && (
          <>
            <p style={styles.help}>
              These values populate the controlled-document header and are reused
              throughout the body (configuration tables, referenced documents,
              traceability, and the product token in deviation / test-case IDs).
            </p>
            <div style={styles.dcGrid}>
              {[
                ['documentTitle', 'Document title', 'Workflow Tracker Verification Protocol'],
                ['documentNumber', 'Document number', 'ISV-R-WF-VEP-1.0'],
                ['revision', 'Revision', 'A'],
                ['effectiveDate', 'Effective date', 'Reference eQMS Release Date'],
                ['systemName', 'System / product name', 'Workflow Tracker Web Application'],
                ['manufacturer', 'Manufacturer', 'iSchemaView'],
                ['versionBuild', 'Version / build', 'v1.0'],
                ['productToken', 'Product code token', 'WT'],
                ['srsReference', 'Referenced SRS', 'ISV-R-WF-SRS Rev A'],
                ['verificationReportId', 'Verification report ID', 'ISV-R-WF-VER-1.0'],
              ].map(([key, labelText, placeholder]) => (
                <label key={key} style={styles.dcField}>
                  <span style={styles.dcLabel}>{labelText}</span>
                  <input
                    style={styles.input}
                    value={docControl[key]}
                    placeholder={placeholder}
                    onChange={(e) => setDc(key, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </>
        )}
      </div>

      {/* JIRA */}
      <div style={styles.section}>
        <label style={styles.label}>JIRA source</label>
        {!selectedProject ? (
          <p style={styles.help}>Select a project at the top to search its tickets.</p>
        ) : (
          <>
            <select
              style={styles.input}
              value={jiraTypeFilter}
              onChange={(e) => setJiraTypeFilter(e.target.value)}
              disabled={loadingIssues || availableTypes.length === 0}
            >
              <option value="">All types ({projectIssues.length})</option>
              {availableTypes.map((type) => {
                const count = projectIssues.filter((i) => i.fields?.issuetype?.name === type).length;
                return <option key={type} value={type}>{type} ({count})</option>;
              })}
            </select>
            <input
              style={{ ...styles.input, marginTop: 8 }}
              placeholder="🔍 Search tickets by key or summary…"
              value={jiraSearch}
              onChange={(e) => setJiraSearch(e.target.value)}
              disabled={loadingIssues}
            />
            <select
              style={{ ...styles.input, marginTop: 8 }}
              value=""
              onChange={(e) => { addTicket(e.target.value); setJiraSearch(''); }}
              disabled={loadingIssues || projectIssues.length === 0}
            >
              <option value="">
                {loadingIssues
                  ? '⏳ Loading tickets…'
                  : `— Add a ticket (${filteredIssues.length}${jiraSearch ? ' matching' : ` of ${projectIssues.length}`}) —`}
              </option>
              {groupedTicketOptions}
            </select>
            {issuesError && <p style={styles.help}>⚠ {issuesError}</p>}
            {selectedTickets.length > 0 && (
              <div style={styles.chipWrap}>
                {selectedTickets.map((t) => (
                  <span key={t.key} style={styles.chip} title={t.summary}>
                    {t.key}{t.type ? ` · ${t.type}` : ''}
                    <button style={styles.chipX} onClick={() => removeTicket(t.key)} title="Remove">×</button>
                  </span>
                ))}
              </div>
            )}
            <p style={styles.help}>Selecting an Epic automatically includes its child stories.</p>
          </>
        )}
      </div>

      {/* GitHub */}
      <div style={styles.section}>
        <label style={styles.label}>GitHub Reference</label>

        <input
          style={styles.input}
          placeholder="🔍 Search repositories…"
          value={repoSearch}
          onChange={(e) => setRepoSearch(e.target.value)}
          disabled={loadingRepos}
        />
        <select
          style={{ ...styles.input, marginTop: 8 }}
          value={selectedRepo}
          onChange={(e) => setSelectedRepo(e.target.value)}
          disabled={loadingRepos}
        >
          <option value="">
            {loadingRepos ? '⏳ Loading repositories…' : `— Select a repository (${filteredRepos.length}${repoSearch ? ' matching' : ''}) —`}
          </option>
          {filteredRepos.map((r) => (
            <option key={r.fullName} value={r.fullName}>{r.fullName}</option>
          ))}
        </select>

        {selectedRepo && (
          <>
            <label style={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={autoSelectRepo}
                onChange={(e) => setAutoSelectRepo(e.target.checked)}
              />
              <span>🤖 Auto-analyze the whole repo (let the AI scan it and pick the relevant files)</span>
            </label>

            <select
              style={{ ...styles.input, marginTop: 8 }}
              value={selectedBranch}
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={loadingBranches}
            >
              <option value="">{loadingBranches ? '⏳ Loading branches…' : '(default branch)'}</option>
              {branches.map((b) => (
                <option key={b.name} value={b.name}>{b.name}</option>
              ))}
            </select>

            <div style={styles.browser}>
              <div style={styles.breadcrumb}>
                <button style={styles.crumb} onClick={() => openDir('')}>root</button>
                {pathSegments.map((seg, i) => (
                  <span key={pathSegments.slice(0, i + 1).join('/')}>
                    <span style={styles.crumbSep}>/</span>
                    <button style={styles.crumb} onClick={() => openDir(pathSegments.slice(0, i + 1).join('/'))}>{seg}</button>
                  </span>
                ))}
              </div>

              <div style={styles.row}>
                <input
                  style={{ ...styles.input, flex: 2 }}
                  placeholder="🔍 Filter files in this folder…"
                  value={ghFileFilter}
                  onChange={(e) => setGhFileFilter(e.target.value)}
                />
                <select
                  style={{ ...styles.input, flex: 1 }}
                  value={ghExtFilter}
                  onChange={(e) => setGhExtFilter(e.target.value)}
                  disabled={fileExtensions.length === 0}
                >
                  <option value="">All types ({fileEntries.length})</option>
                  {fileExtensions.map((ext) => {
                    const count = fileEntries.filter((f) => extOf(f.name) === ext).length;
                    return <option key={ext} value={ext}>{ext} ({count})</option>;
                  })}
                </select>
              </div>

              {loadingDir ? (
                <p style={styles.help}>⏳ Loading…</p>
              ) : ghDirError ? (
                <p style={styles.help}>⚠ {ghDirError}</p>
              ) : (
                <ul style={styles.fileList}>
                  {dirEntries.map((d) => (
                    <li key={d.path} style={styles.fileItem}>
                      <button style={styles.dirBtn} onClick={() => openDir(d.path)}>📁 {d.name}</button>
                    </li>
                  ))}
                  {visibleFiles.map((f) => {
                    const added = selectedFiles.includes(f.path);
                    return (
                      <li key={f.path} style={styles.fileItem}>
                        <span>📄 {f.name}</span>
                        <button style={styles.linkBtn} onClick={() => (added ? removeFile(f.path) : addFile(f.path))}>
                          {added ? 'remove' : 'add'}
                        </button>
                      </li>
                    );
                  })}
                  {dirEntries.length === 0 && visibleFiles.length === 0 && (
                    <li style={styles.fileItem}><span style={styles.help}>No matching files in this folder.</span></li>
                  )}
                </ul>
              )}
            </div>

            {selectedFiles.length > 0 ? (
              <div style={styles.chipWrap}>
                {selectedFiles.map((p) => (
                  <span key={p} style={styles.chip} title={p}>
                    {p}
                    <button style={styles.chipX} onClick={() => removeFile(p)} title="Remove">×</button>
                  </span>
                ))}
              </div>
            ) : (
              <p style={styles.help}>
                {autoSelectRepo
                  ? 'Auto-analyze is on — the AI will scan this repo and pick the relevant files. Optionally click “add” to force-include specific files.'
                  : 'No files selected yet — click “add” next to a file above to include it.'}
              </p>
            )}
          </>
        )}
      </div>

      {/* Supporting / uploaded docs */}
      <div style={styles.section}>
        <label style={styles.label}>{mode === 'review' ? 'Supporting documents (optional reference)' : 'Uploaded documents (optional reference)'}</label>
        <p style={styles.help}>
          {mode === 'review'
            ? 'Extra reference material that informs the review of the base document above — for example the SRS this document should align to, or related specs. These are read as context only; the document being revised is the one you uploaded under “Review / update existing.”'
            : 'Existing source material to fold into the new document — for example a stakeholder-needs doc, an existing SRS (to build a VEP from), API specs, or design notes. The AI reads these as source content to draw from; it does not edit or return them. Word (.docx), text, Markdown, JSON, and CSV are supported.'}
        </p>
        <input ref={docsInputRef} type="file" multiple accept=".txt,.md,.json,.csv,.docx,text/*" onChange={handleDocsUpload} style={styles.fileInput} />
        {uploadedDocs.length > 0 && (
          <ul style={styles.docList}>
            {uploadedDocs.map((d, i) => (
              <li key={`${d.name}-${i}`} style={styles.docItem}>
                <span>{d.name} ({d.content.length} chars)</span>
                <button style={styles.linkBtn} onClick={() => removeDoc(i)}>remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Notes / Raw Speech — typed or dictated context folded into generation. */}
      <div style={styles.section}>
        <label style={styles.label}>
          Notes / Raw speech (optional)
          {dictation.supported && (
            <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: dictation.listening ? '#ef5350' : '#888' }}>
              {dictation.listening ? '● Listening — click the mic to stop' : '🎤 Click the mic to dictate'}
            </span>
          )}
        </label>
        <div style={styles.notesRow}>
          <textarea
            style={styles.notesArea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Type or dictate any context, requirements, or spoken notes to fold into this deliverable…"
            rows={4}
          />
          <button
            type="button"
            onClick={dictation.toggle}
            disabled={!dictation.supported}
            title={!dictation.supported ? 'Voice input needs Chrome or Edge' : (dictation.listening ? 'Stop dictation' : 'Start dictation')}
            aria-label={dictation.listening ? 'Stop dictation' : 'Start dictation'}
            style={{
              ...styles.micBtn,
              cursor: dictation.supported ? 'pointer' : 'not-allowed',
              backgroundColor: dictation.listening ? '#ef5350' : (dictation.supported ? '#3a3a4a' : '#2a2a2a'),
              animation: dictation.listening ? 'lyricMicPulse 1.5s infinite' : 'none',
            }}
          >
            {dictation.listening ? '⏹' : '🎤'}
          </button>
        </div>
        {dictation.error && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 6 }}>⚠️ {dictation.error}</div>}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <button style={{ ...styles.generateBtn, ...(loading ? styles.btnDisabled : {}) }} onClick={handleGenerate} disabled={loading}>
        {loading
          ? (mode === 'review' ? 'Reviewing…' : 'Generating…')
          : (mode === 'review' ? 'Review & update document' : 'Generate document')}
      </button>

      {/* Result. In flow mode the document is editable and can be saved back to
          the flow; standalone keeps the rendered HTML preview. */}
      {(result || (lockedDocType && editMarkdown)) && (
        <div style={styles.resultSection}>
          <div style={styles.resultHeader}>
            <span style={styles.resultTitle}>
              {result
                ? (result.mode === 'review'
                  ? `Revised: ${result.baseDocumentName || 'document'}${result.templateName ? ` · ${result.templateName}` : ''}`
                  : `${result.docType} · ${result.templateName}`)
                : `${flowStep?.name || lockedDocType} (saved draft)`}
            </span>
            <div>
              <button style={styles.secondaryBtn} onClick={handleCopyMarkdown}>Copy Markdown</button>
              <button style={{ ...styles.secondaryBtn, ...(docxLoading ? styles.btnDisabled : {}) }} onClick={() => openDownloadPrompt('docx')} disabled={docxLoading}>
                {docxLoading ? 'Building…' : 'Download .docx'}
              </button>
              <button style={{ ...styles.secondaryBtn, ...(pdfLoading ? styles.btnDisabled : {}) }} onClick={() => openDownloadPrompt('pdf')} disabled={pdfLoading}>
                {pdfLoading ? 'Rendering…' : 'Download PDF'}
              </button>
            </div>
          </div>

          {sources && (
            <div style={styles.sourcesBar}>
              {sources.jira?.used?.length > 0 && <span style={styles.sourceTag}>JIRA: {sources.jira.used.join(', ')}</span>}
              {sources.github?.used?.length > 0 && <span style={styles.sourceTag}>Repo: {sources.github.used.join(', ')}</span>}
              {sources.uploadedDocs?.length > 0 && <span style={styles.sourceTag}>Docs: {sources.uploadedDocs.join(', ')}</span>}
              {[...(sources.jira?.errors || []), ...(sources.github?.errors || [])].map((e, i) => (
                <span key={i} style={styles.sourceErr}>⚠ {e}</span>
              ))}
            </div>
          )}

          {onSaveDeliverable && lockedDocType ? (
            <div style={styles.editWrap}>
              <p style={styles.help}>Edit the document below, then save it to the flow. Markdown is supported.</p>
              <textarea
                style={styles.editArea}
                value={editMarkdown}
                onChange={(e) => { setEditMarkdown(e.target.value); setSaveState('idle'); }}
                rows={20}
              />
              <div style={styles.saveRow}>
                <button
                  style={{ ...styles.saveBtn, ...(saveState === 'saving' ? styles.btnDisabled : {}) }}
                  onClick={handleSaveToFlow}
                  disabled={saveState === 'saving'}
                >
                  {saveState === 'saving' ? 'Saving…' : `💾 Save to ${flowName || 'flow'}`}
                </button>
                {saveState === 'saved' && <span style={styles.savedNote}>✓ Saved to flow</span>}
                {saveState === 'error' && <span style={styles.sourceErr}>Save failed</span>}
              </div>
            </div>
          ) : (
            <div style={styles.preview} dangerouslySetInnerHTML={{ __html: result?.html || '' }} />
          )}
        </div>
      )}

      <TemplateLibrary
        open={libraryOpen}
        onClose={() => { setLibraryOpen(false); setLibraryPrefill(null); }}
        apiUrl={apiUrl}
        authenticatedFetch={authenticatedFetch}
        templates={savedTemplates}
        reload={loadSavedTemplates}
        docTypeDefault={lockedDocType || 'generic'}
        prefill={libraryPrefill}
        onUse={applySavedTemplate}
      />

      {downloadPrompt && (
        <div style={styles.modalOverlay} onClick={() => setDownloadPrompt(null)}>
          <div
            style={styles.modalBox}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-modal-title"
          >
            <h3 id="download-modal-title" style={styles.modalTitle}>Download {downloadPrompt.format.toUpperCase()}</h3>
            <p style={styles.help}>Name the file. Defaults to the document number / template name — edit as needed.</p>
            <div style={styles.modalInputRow}>
              <input
                style={styles.input}
                value={filenameInput}
                autoFocus
                aria-label="File name"
                onChange={(e) => setFilenameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmDownload(); if (e.key === 'Escape') setDownloadPrompt(null); }}
              />
              <span style={styles.modalExt}>.{downloadPrompt.format}</span>
            </div>
            <div style={styles.modalActions}>
              <button style={styles.secondaryBtn} onClick={() => setDownloadPrompt(null)}>Cancel</button>
              <button style={styles.primaryBtn} onClick={confirmDownload}>⬇ Download</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: 900, margin: '0 auto', padding: '8px 4px', fontFamily: 'inherit' },
  title: { fontSize: 22, margin: '0 0 4px', color: '#222' },
  subtitle: { color: '#555', marginTop: 0, marginBottom: 16, fontSize: 14 },
  modeToggle: { display: 'flex', gap: 8, marginBottom: 16 },
  modeBtn: { flex: 1, padding: '10px 12px', border: '1px solid #ccc', borderRadius: 8, background: '#fff', color: '#333', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  modeBtnActive: { background: '#1a56db', color: '#fff', borderColor: '#1a56db' },
  section: { marginBottom: 18, padding: 14, border: '1px solid #e3e3e3', borderRadius: 8, background: '#fafafa' },
  label: { display: 'block', fontWeight: 600, marginBottom: 8, fontSize: 14, color: '#333' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' },
  row: { display: 'flex', gap: 8 },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 8px', color: '#333', fontSize: 13, cursor: 'pointer' },
  collapseHeader: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: 0, fontWeight: 600, fontSize: 14, color: '#333', cursor: 'pointer' },
  dcGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginTop: 10 },
  dcField: { display: 'flex', flexDirection: 'column', gap: 4 },
  dcLabel: { fontSize: 12, fontWeight: 600, color: '#333' },
  help: { color: '#777', fontSize: 12, margin: '6px 0 0' },
  uploadRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' },
  fileInput: { fontSize: 13 },
  pillRow: { display: 'flex', alignItems: 'center', gap: 10 },
  pill: { background: '#e8f0fe', color: '#1a56db', padding: '4px 10px', borderRadius: 12, fontSize: 13 },
  builtInBadge: { background: '#e6f4ea', color: '#137333', padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  chipWrap: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#e8f0fe', color: '#1a56db', padding: '4px 8px', borderRadius: 12, fontSize: 13 },
  chipX: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: 0 },
  browser: { marginTop: 10, border: '1px solid #ddd', borderRadius: 6, background: '#fff', padding: 10 },
  breadcrumb: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', marginBottom: 8, fontSize: 13 },
  crumb: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13, padding: '0 2px' },
  crumbSep: { color: '#999', margin: '0 2px' },
  fileList: { listStyle: 'none', padding: 0, margin: '8px 0 0', maxHeight: 240, overflowY: 'auto' },
  fileItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 2px', fontSize: 13, borderBottom: '1px solid #f0f0f0', color: '#333' },
  dirBtn: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13, padding: 0, textAlign: 'left' },
  linkBtn: { background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', padding: 0 },
  docList: { listStyle: 'none', padding: 0, margin: '10px 0 0' },
  docItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 13 },
  error: { background: '#fde8e8', color: '#9b1c1c', padding: '10px 12px', borderRadius: 6, marginBottom: 14, fontSize: 14 },
  generateBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', width: '100%' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  resultSection: { marginTop: 22, border: '1px solid #e3e3e3', borderRadius: 8, overflow: 'hidden' },
  resultHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#f3f4f6', borderBottom: '1px solid #e3e3e3' },
  resultTitle: { fontWeight: 600, fontSize: 14, color: '#222' },
  secondaryBtn: { background: '#fff', border: '1px solid #ccc', padding: '6px 12px', borderRadius: 6, fontSize: 13, cursor: 'pointer', marginLeft: 8, color: '#333' },
  sourcesBar: { display: 'flex', flexWrap: 'wrap', gap: 8, padding: '8px 14px', background: '#fbfbfb', borderBottom: '1px solid #eee' },
  sourceTag: { background: '#eef2ff', color: '#3730a3', padding: '2px 8px', borderRadius: 10, fontSize: 12 },
  sourceErr: { background: '#fff7ed', color: '#9a3412', padding: '2px 8px', borderRadius: 10, fontSize: 12 },
  preview: { padding: 18, maxHeight: 600, overflowY: 'auto', background: '#fff', lineHeight: 1.55, color: '#1a1a1a' },
  inputHints: { border: '1px solid #dbe4ff', background: '#f5f8ff', borderRadius: 8, padding: '10px 12px', marginBottom: 16 },
  hintRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 6 },
  hintReqLabel: { fontSize: 11, fontWeight: 700, color: '#9b1c1c', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 },
  hintOptLabel: { fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: 0.4, marginRight: 4 },
  hintReq: { background: '#fde8e8', color: '#9b1c1c', padding: '2px 8px', borderRadius: 10, fontSize: 12 },
  hintOpt: { background: '#eef2ff', color: '#3730a3', padding: '2px 8px', borderRadius: 10, fontSize: 12 },
  notesRow: { display: 'flex', gap: 10, alignItems: 'flex-start' },
  notesArea: { flex: 1, padding: '8px 10px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' },
  micBtn: { width: 44, height: 44, flexShrink: 0, borderRadius: '50%', border: 'none', color: '#fff', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  editWrap: { padding: 14, background: '#fff' },
  editArea: { width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 13, fontFamily: 'SFMono-Regular, Consolas, monospace', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5 },
  saveRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 },
  saveBtn: { background: '#047857', color: '#fff', border: 'none', padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  savedNote: { color: '#047857', fontSize: 13, fontWeight: 600 },
  primaryBtn: { background: '#1a56db', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer', marginLeft: 8 },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalBox: { background: '#fff', borderRadius: 10, padding: 20, width: 'min(480px, 92vw)', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' },
  modalTitle: { margin: '0 0 4px', fontSize: 17, color: '#111' },
  modalInputRow: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 },
  modalExt: { color: '#666', fontSize: 14, fontWeight: 600 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', marginTop: 16 },
};

export default QaDocForm;
